import {
  CommonMetadataFields,
  FaviconComponentProps,
  ISODateObject,
} from "types/Metadata";
import { TaxonomyItem } from "types/Search/TaxonomyItem";
import { capitalizeLanguageRegion } from "./localeUtils";

export const CoveoFieldName = {
  // Article
  ArticleAuthor: "@article_author",

  // ClickableUri
  ClickableUri: "@clickableuri",

  // Event fields
  EventDescription: "@event_description",
  EventEnd: "@event_end",
  EventImage: "@event_image",
  EventLocation: "@event_location",
  EventStart: "@event_start",
  EventSubtitle: "@event_subtitle",
  EventTimezone: "@event_timezone",
  EventTitle: "@event_title",
  EventType: "@event_type",

  // Page fields
  PageExclude: "@page_exclude",
  PageFather: "@page_father",
  PageId: "@page_id",
  PageParentId: "@page_parent_id",
  PageLang: "@page_lang",
  PagePublicationDate: "@page_publication_date",
  PagePublicationDay: "@page_publication_day",
  PagePublicationMonth: "@page_publication_month",
  PagePublicationYear: "@page_publication_year",
  PageTemplateId: "@page_template_id",
  PageTemplateName: "@page_template_name",

  // Taxonomy fields
  TaxonomyApplications: "@taxonomy_applications",
  TaxonomyArticleTopics: "@taxonomy_article_topics",
  TaxonomyArticleTypes: "@taxonomy_article_types",
  TaxonomyNewsLetterNames: "@taxonomy_newsletter_names",
  TaxonomyCompanies: "@taxonomy_companies",
  TaxonomyCountries: "@taxonomy_countries",
  TaxonomyGaGroup: "@taxonomy_ga_group",
  TaxonomyIndustries: "@taxonomy_industries",
  TaxonomyLocationRegions: "@taxonomy_location_regions",
  TaxonomyLocationTypes: "@taxonomy_location_types",
  TaxonomyMarkets: "@taxonomy_markets",
  TaxonomyNewsTopics: "@taxonomy_news_topics",
  TaxonomyNewsTypes: "@taxonomy_news_types",
  TaxonomySolutions: "@taxonomy_solutions",
  TaxonomyStoryTopics: "@taxonomy_story_topics",
  TaxonomyStoryTypes: "@taxonomy_story_types",
  TaxonomyTranslationOwner: "@taxonomy_translation_owner",
  TaxonomyOfferingType: "@taxonomy_offering_type",
  TaxonomyRegions: "@taxonomy_regions",
  TaxonomyGoogleAnalyticsRepGroup: "@taxonomy_ga_group",
} as const;

// Type for the field values (e.g., '@page_template_name')
export type CoveoFieldNameValue =
  (typeof CoveoFieldName)[keyof typeof CoveoFieldName];

// Type for the field keys (e.g., 'PageTemplateName')
export type CoveoFieldNameKey = keyof typeof CoveoFieldName;

/**
 * Percent-encode a single hash-fragment value so taxonomy labels containing
 * spaces, slashes, etc. survive URL round-trips.
 */
export const encodeHashValue = (value: string): string =>
  encodeURIComponent(value);

/**
 * Build the Coveo facet hash fragment for taxonomy applications/solutions.
 * Returns an empty string when both arrays are empty so callers can simply
 * concatenate.
 */
export const buildHashUrl = (
  applications: string[],
  solutions: string[],
  industries: string[],
  markets: string[],
): string => {
  const hashParts: string[] = [];
  if (applications.length > 0) {
    hashParts.push(`f-taxonomy_applications=${applications.join(",")}`);
  }
  if (solutions.length > 0) {
    hashParts.push(`f-taxonomy_solutions=${solutions.join(",")}`);
  }
  if (industries.length > 0) {
    hashParts.push(`f-taxonomy_industries=${industries.join(",")}`);
  }
  if (markets.length > 0) {
    hashParts.push(`f-taxonomy_markets=${markets.join(",")}`);
  }
  return hashParts.length > 0 ? `#${hashParts.join("&")}` : "";
};

export const castToXMCTaxonomyItems = (items?: TaxonomyItem[]): string[] => {
  if (!items || items?.length == 0) return [];
  return items.map((item) => item.fields.TaxonomyLabel.value);
};

export function toISODateFromISOString(
  isoString: string,
): ISODateObject | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?Z$/.exec(
    isoString.trim(),
  );
  if (!m) return null;

  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);

  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;

  const mm = String(month).padStart(2, "0");
  const dd = String(day).padStart(2, "0");

  return {
    isoDate: `${year}-${mm}-${dd}`,
    year: year.toString(),
    month: mm,
    day: dd,
  };
}

export function toISODate(mmddyyyy: string): ISODateObject | null {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(mmddyyyy.trim());
  if (!m) return null;

  const month = Number(m[1]);
  const day = Number(m[2]);
  const year = Number(m[3]);

  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;

  const mm = String(month).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return {
    isoDate: `${year}-${mm}-${dd}`,
    year: year.toString(),
    month: mm,
    day: dd,
  };
}

export const getCommonMetadata = (props: CommonMetadataFields) => {
  const publicationDate = props.publicationDate
    ? toISODate(props.publicationDate) ||
      toISODateFromISOString(props.publicationDate)
    : null;

  const robotsSetByEnv =
    process.env.NEXT_PUBLIC_DISABLE_CRAWLERS === "true"
      ? {
          "robots-set-by-environment":
            "The robots tag immediately preceeding this is being set by an environment configuration to prevent indexing",
        }
      : {};

  return {
    coveobot: "all",
    ecolab_src: props.src,
    ecolab_page_lang: props.language,
    ecolab_page_id: props.id,
    ecolab_page_template_id: props?.template?.id,
    ecolab_page_template_name: props?.template?.name,
    ecolab_page_exclude: (props?.excludeFromSearch || false).toString(),
    ecolab_taxonomy_solutions: props?.solutions,
    ecolab_taxonomy_applications: props?.applications,
    ecolab_taxonomy_markets: props?.markets,
    ecolab_taxonomy_industries: props?.industries,
    ecolab_taxonomy_companies: props?.companies,
    ecolab_taxonomy_countries: props?.countries,
    ecolab_taxonomy_regions: props?.regions,
    ecolab_taxonomy_translation_owner: props?.translationOwner,
    ecolab_taxonomy_ga_group: props?.googleAnalyticsRepGroup,
    ecolab_publication_date: publicationDate?.isoDate,
    ecolab_publication_year: publicationDate?.year,
    ecolab_publication_month: publicationDate?.month,
    ecolab_publication_day: publicationDate?.day,
    ...robotsSetByEnv,
  };
};

export const getFavicons = (componentProps: FaviconComponentProps) => {
  const name = componentProps?.fields?.applicationName?.value;
  const tileColor = componentProps?.fields?.tileColor?.value;
  return {
    icons: {
      icon: [
        {
          url: "/favicon/favicon-32x32.png",
          sizes: "32x32",
          type: "image/png",
        },
        {
          url: "/favicon/favicon-16x16.png",
          sizes: "16x16",
          type: "image/png",
        },
      ],
      apple: [
        {
          url: "/favicon/apple-touch-icon.png",
          sizes: "180x180",
          type: "image/png",
        },
      ],
    },
    manifest: "/favicon/site.webmanifest",
    ...(name && {
      "application-name": name,
    }),
    ...(tileColor && {
      "msapplication-TileColor": tileColor,
    }),
  };
};

export const getRobotsConfig = (isNoIndex = false, isNoFollow = false) =>
  process.env.NEXT_PUBLIC_DISABLE_CRAWLERS === "true"
    ? {
        index: false,
        follow: false,
      }
    : {
        index: !isNoIndex,
        follow: !isNoFollow,
      };

/**
 * Coveo Atomic UI language (2-letter codes for i18next)
 * Used for: AtomicSearchInterface `language` prop
 */
export function toCoveoUILanguage(appLocale: string): string {
  const specialCases: Record<string, string> = {
    en: "en-US",
    "en-us": "en-US",
  };
  const lower = appLocale.toLowerCase();
  if (specialCases[lower]) return specialCases[lower];
  return capitalizeLanguageRegion(appLocale);
}
